import type { SectionProps } from "../context";
import { field, messageVars } from "../context";
import { waLink } from "../helpers";
import { Icon } from "../icons";
import { CARD_HOVER, Container, MultiLine, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

/**
 * Bank accounts / e-wallets for gifts (amplop digital), plus an optional address for parcels.
 * No JavaScript: the number is `user-select: all`, so one tap selects it for copying.
 */
export function Gift({ node, ctx }: SectionProps<"gift">) {
  const c = node.content;
  const confirm = waLink(ctx.site.contact.whatsapp, "Halo {{nama_usaha}}, saya sudah mengirim tanda kasih atas nama ...", messageVars(ctx));
  const n = c.accounts.length + (c.address ? 1 : 0);
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align="center" />
        <ul className={cx(STACK, STAGGER, "grid gap-5 @3xl:gap-6", n <= 1 ? "mx-auto max-w-md" : n === 2 ? "mx-auto max-w-3xl @2xl:grid-cols-2" : "@2xl:grid-cols-2 @4xl:grid-cols-3")}>
          {c.accounts.map((acc, i) => (
            <li key={i}>
              <div className={cx("relative isolate flex aspect-[1.586/1] min-h-48 flex-col overflow-hidden rounded-site-lg bg-site-primary p-6 text-site-on-primary shadow-site-md", CARD_HOVER)}>
                <span aria-hidden="true" className="absolute -top-16 -right-12 -z-10 size-48 rounded-full bg-site-on-primary/10" />
                <span aria-hidden="true" className="absolute -bottom-20 -left-10 -z-10 size-56 rounded-full bg-site-on-primary/5" />
                <div className="flex items-start justify-between gap-3">
                  <p className="text-lg font-semibold tracking-wide" {...field(ctx, `content.accounts.${i}.provider`)}>
                    {acc.provider}
                  </p>
                  <span aria-hidden="true" className="grid size-9 place-items-center rounded-full bg-site-on-primary/15">
                    <Icon name="gift" className="size-[1.125rem]" />
                  </span>
                </div>
                <div className="mt-auto">
                  <p className="text-[0.6875rem] font-semibold tracking-[0.18em] uppercase opacity-80">Nomor</p>
                  <p className="baze-select-all mt-1 font-mono text-xl font-semibold tracking-wider tabular-nums @3xl:text-[1.375rem]" {...field(ctx, `content.accounts.${i}.number`)}>
                    {acc.number}
                  </p>
                  <p className="mt-3 text-sm opacity-90">
                    a.n. <span {...field(ctx, `content.accounts.${i}.name`)}>{acc.name}</span>
                  </p>
                </div>
              </div>
            </li>
          ))}
          {c.address ? (
            <li>
              <div className={cx("flex h-full min-h-48 flex-col rounded-site-lg border border-site-border bg-site-surface p-6 shadow-site-sm", CARD_HOVER)}>
                <span aria-hidden="true" className="grid size-11 place-items-center rounded-full bg-site-primary/10 text-site-primary">
                  <Icon name="map-pin" className="size-5" />
                </span>
                <p className="mt-4 font-semibold text-site-text">Kirim kado ke alamat</p>
                <MultiLine ctx={ctx} text={c.address} path="content.address" className="mt-2 leading-relaxed text-site-muted" />
              </div>
            </li>
          ) : null}
        </ul>
        {c.accounts.length > 0 ? (
          <p className="mt-6 text-center text-sm text-site-muted">Ketuk nomor untuk memilih, lalu salin.</p>
        ) : null}
        {confirm ? (
          <div className="mt-6 flex justify-center">
            <SiteLink
              ctx={ctx}
              href={confirm}
              whatsapp
              className="inline-flex min-h-11 items-center gap-2 font-semibold text-site-primary underline-offset-4 hover:underline"
            >
              <Icon name="whatsapp" className="size-4" />
              Konfirmasi via WhatsApp
            </SiteLink>
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}
